import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { styled } from "@mui/material";
import {
  QualifiedFeature,
  SortType,
  SummaryDiseaseClusterFragment,
} from "fe-common/graphql";
import { usePatientSummary } from "fe-common/patientSummary/useFetchPatientSummary";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";
import { DiseaseZoomData } from "fe-web-components/dialogs/DiseaseZoom";
import clsx from "clsx";
import { featureToLines } from "fe-common/logic";
import { NavigationRecommendationIndicator } from "fe-web-components/navigationRecommendation/navigationRecommendation";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { KahunTitleCard } from "fe-web-components/layout/kahunTitleCard";
import { CommonColors } from "fe-web-components/colors/commonColors";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import {
  PatientCardCommonActions,
  PatientCardSummaryOrChartToggle,
} from "./patientCardSummaryOrChartToggle";
import { CommonPatientCardDd } from "./commonPatientCardDd";
import { PatientCardAgeAndGender } from "./patientCardAgeAndGender";
import { ThemeSettings } from "@kahunmedical/common-configuration";
import { PatientInfo } from "./patientInfo";
import { PatientRecommendations } from "./patientRecommendations";
import { PatientCardCreateAssesment } from "./patientCardCreateAssesment";
import {
  onAddonAndThemeBreakPoint,
  PATIENT_CARD_HEADER_DESKTOP_HEIGHT,
} from "./patientCardCommonUi";
import { KAHUN_PLATFORM } from "user-management/src/components/loginForm";
import {
  MobileSectionInfoText,
  PatientCardSectionTemplate,
} from "./layout/patientCardSectionTemplate";
import { GroupedWorkupList } from "./groupedWorkupList";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { useClinicState } from "ask/src/clinic/clinicStateContext";
//region [[ Styles ]]

const PatientCardRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "6px 30px",
  width: "100%",
  // position: "relative",
  ...onAddonAndThemeBreakPoint(theme.breakpoints.down("sm"), {
    flexDirection: "column",
    overflow: "initial",
  }),
}));

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
});

const PatientInfoSection = styled("div")(({ theme }) => ({
  flex: 1,
  ...scrollableStyle,
  maxHeight: `calc(100vh - ${PATIENT_CARD_HEADER_DESKTOP_HEIGHT + 30}px)`,
  ...onAddonAndThemeBreakPoint(theme.breakpoints.down("md"), {
    maxHeight: "fit-content",
    overflow: "initial",
  }),
}));

const Summary = styled(PatientCardSummaryOrChartToggle)({
  position: "static",
  "& .gender-icon-view": {
    margin: 0,
  },
  "& .gender-icon-container": {
    padding: 0,
  },
  "& .gender-text": {
    margin: "0 12px",
    fontWeight: 700,
    color: CommonColors.Primary,
  },
  marginTop: "6px",
});

const RecommendationsCard = styled(KahunTitleCard)({});

const CloseButton = styled(CloseIconButton)({
  left: "auto",
  right: "10px",
  justifyContent: "right",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientCardTopAction {
  onClick: (e: React.MouseEvent<HTMLButtonElement>, PatientSummaryData) => void;
  element: React.ReactNode;
}

export interface PatientRowExpandedProps {
  className?: string;
  caseId: string;
  clinicId: string;
  clinicName?: string;
  disableAssessment?: boolean;
  disableSuggestions?: boolean;
  onClick?: (e) => void;
  onClose?: () => void;
  themeSettings: ThemeSettings;
  supportedSections: string[];
  disableTooltip?: boolean;
  showPatientInfo?: boolean;
  actions?: PatientCardCommonActions[];
  patientCase: {
    main: QualifiedFeature[];
    clinicalFeatures: QualifiedFeature[];
    reviewOfSystems: QualifiedFeature[];
    background: QualifiedFeature[];
  };
  topActions?: PatientCardTopAction[];
  presentIcdCodes?: boolean;
  platform?: KAHUN_PLATFORM;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const PatientCardCommon = ({
  caseId,
  clinicId,
  showPatientInfo = true,
  clinicName,
  topActions,
  presentIcdCodes = true,
  platform = KAHUN_PLATFORM.UNKNOWN,
  ...props
}: PatientRowExpandedProps) => {
  const allowFeatureClick = !!getFeatureFlags("PatientCardFeatureClick");

  const [diseaseZoom, setDiseaseZoom] = useState({
    open: false,
    selection: null as DiseaseZoomData,
  });

  const { locale, translate } = useTranslateByGender();
  const clinicState = useClinicState();
  const clinicConfig = clinicState?.[0].clinicConfig;

  const containerRef = useRef<HTMLDivElement>();

  const patientSummary = usePatientSummary(
    caseId,
    clinicId,
    undefined,
    true,
    clinicConfig?.[ClinicConfigKeys.UsesLLM], //use LLM polling
    clinicConfig?.[ClinicConfigKeys.UsesRag],
    clinicConfig?.[ClinicConfigKeys.GroundAssessment],
    clinicConfig?.[ClinicConfigKeys.GroundExplanation],
  ); //TODO: set llm and grounding according to clinicConfig
  //In Hebrew also use DD in English
  const patientSummaryEn = usePatientSummary(
    locale === "he" ? caseId : null,
    clinicId,
    "en",
  );

  const dd = patientSummaryEn?.possibleCause || patientSummary?.possibleCause;
  const problemList = patientSummary?.problemList;

  const getSelectionData = useCallback(
    (cluster: SummaryDiseaseClusterFragment) => {
      return {
        diseaseName: featureToLines(cluster.title).toString(),
        diseaseId: cluster.title.id,
        crs: null,
        citations: null,
      };
    },
    [],
  );

  const handleDiseaseClicked = useCallback(
    (cluster: SummaryDiseaseClusterFragment) => {
      setDiseaseZoom({
        open: true,
        selection: getSelectionData(cluster),
      });
    },
    [getSelectionData],
  );

  const loading = patientSummary?.loading;

  useEffect(() => {
    if (!loading && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [loading]);

  if (loading) {
    return <ThreeDotsLoader />;
  }

  const recommendations = patientSummary?.recommendations?.filter(
    (r) =>
      r.feature?.feature.id !==
      patientSummary?.navigationAdvice?.feature?.feature?.id,
  );

  const showPatientCardAgeAndGender =
    patientSummary?.summary?.age || patientSummary?.summary?.main;

  const handleTopActionClicked = (index: number, e: React.MouseEvent<any>) => {
    topActions?.[index].onClick?.(e, patientSummary);
  };

  return (
    <Container
      dir={locale === "he" ? "rtl" : "ltr"}
      className={clsx("patient-row-expanded", props.className)}
      onClick={props.onClick}
    >
      <PatientCardRow className={"patient-info-wrapper"} ref={containerRef}>
        {!!props.onClose && <CloseButton onClick={props.onClose} />}
        {showPatientInfo && (
          <PatientInfoSection>
            <MobileSectionInfoText>
              {translate("PATIENT_INFORMATION")}
            </MobileSectionInfoText>
            {showPatientCardAgeAndGender && (
              <KahunTitleCard
                title={
                  <PatientCardAgeAndGender
                    themeSettings={props.themeSettings}
                    summary={patientSummary?.summary}
                  />
                }
              >
                <PatientInfo summary={patientSummary?.summary} />
              </KahunTitleCard>
            )}

            {patientSummary?.navigationAdvice && (
              <RecommendationsCard title={translate("RECOMMENDATIONS")}>
                <NavigationRecommendationIndicator
                  feature={patientSummary.navigationAdvice.feature?.feature}
                />
                <PatientRecommendations recommendations={recommendations} />
              </RecommendationsCard>
            )}
          </PatientInfoSection>
        )}
        <PatientCardSectionTemplate
          flex={2}
          title={
            <>
              <span>{translate("SUMMARY_TITLE")}</span>
              {topActions?.map((act, i) =>
                React.cloneElement(act.element as any, {
                  key: i,
                  onClick: (e) => handleTopActionClicked(i, e),
                }),
              )}
            </>
          }
          content={
            <Summary
              inExtension={props.disableTooltip}
              patientSummary={patientSummary}
              caseId={caseId}
              clinicName={clinicName}
              clinicId={clinicId}
              supportedSections={props.supportedSections}
              patientCase={props.patientCase}
              actions={props.actions}
              platform={platform}
            >
              {showPatientCardAgeAndGender && !showPatientInfo && (
                <PatientCardAgeAndGender
                  themeSettings={props.themeSettings}
                  summary={patientSummary?.summary}
                />
              )}
            </Summary>
          }
        />
        {!!dd && !!dd.length && (
          <PatientCardSectionTemplate
            className={"patient-card-dd"}
            title={translate("DD_TITLE")}
            content={
              <CommonPatientCardDd
                sortType={SortType.PatientProb}
                dd={dd}
                onDiseasesClicked={
                  allowFeatureClick ? handleDiseaseClicked : null
                }
              />
            }
          />
        )}

        {patientSummary?.assessment?.text && (
          <PatientCardSectionTemplate
            title={"Assessment"}
            content={patientSummary?.assessment?.text}
          />
        )}
        {patientSummary?.workup?.text && (
          <PatientCardSectionTemplate
            title={"Suggested Workup"}
            content={patientSummary?.workup?.text}
          />
        )}
        {patientSummary?.workupList && (
          <PatientCardSectionTemplate
            title={"Suggested Workup"}
            content={
              <GroupedWorkupList workupList={patientSummary?.workupList} />
            }
          />
        )}
      </PatientCardRow>
      {problemList && presentIcdCodes && (
        <PatientCardCreateAssesment dd={dd} problemList={problemList} />
      )}
    </Container>
  );
};
